<script setup>
// Page 10 — the ask, put as the question the whole page has been building to. The body
// carries the three setup steps (connect, add units, collect consent) so the close
// doubles as "here is what saying yes costs you" — that used to be its own page of
// three cards, which meant the page explained the setup before it had earned the sale.
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { ArrowRight, MessageCircle } from "@lucide/vue";
import { buttonVariants } from "@/components/ui/button";
import { useMotion, stampIn, vMagnetic } from "@/lib/motion";

const { t } = useI18n();

const root = ref(null);
const ctaPanel = ref(null);

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    stampIn(ctaPanel.value);
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <section ref="root" class="snap-page page-panel flex items-center" :data-page-label="t('landing.pager.cta')">
    <div class="mx-auto w-full max-w-6xl px-5 py-10 sm:py-16">
      <div ref="ctaPanel" class="border-2 border-foreground bg-primary p-6 text-primary-foreground shadow-[var(--shadow-hard-lg)] sm:p-14">
        <h2 class="max-w-3xl font-sans text-2xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">{{ t("landing.cta.title") }}</h2>
        <p class="mt-4 max-w-2xl text-sm leading-relaxed text-primary-foreground/85 sm:mt-5 sm:text-base">{{ t("landing.cta.body") }}</p>
        <div class="mt-6 flex flex-wrap items-center gap-4 sm:mt-9">
          <span v-magnetic class="inline-block">
            <RouterLink :to="{ path: '/signup', query: { voor: 'vve' } }" :class="buttonVariants({ variant: 'outline', size: 'lg' })">
              {{ t("landing.cta.primary") }}<ArrowRight class="size-4" />
            </RouterLink>
          </span>
          <!-- The second option is a question, not a login: someone who reaches the
               bottom unconvinced needs a person, and "Inloggen" is already in both the
               header and the footer for anyone who has an account. -->
          <a href="https://wa.me/31643500374" class="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-foreground underline-offset-4 hover:underline">
            <MessageCircle class="size-4" />{{ t("landing.cta.secondary") }}
          </a>
        </div>
        <p class="mt-4 text-xs text-primary-foreground/85 sm:text-sm">
          {{ t("landing.contact.hint") }}
          <a href="https://wa.me/31643500374" class="font-semibold underline-offset-4 hover:underline">{{ t("landing.contact.whatsapp") }}</a>
          · <a href="tel:+31643500374" class="font-semibold underline-offset-4 hover:underline">{{ t("landing.contact.call") }}</a>
          · <a href="mailto:info@pandig.nl" class="font-semibold underline-offset-4 hover:underline">{{ t("landing.contact.email") }}</a>
        </p>
      </div>
    </div>
  </section>
</template>
